/**
 * BattleModule — the client half of a war: battles you can WATCH.
 *
 * Reads `battle` + `battle_unit` rows and, for every battle within range:
 *   - draws each fighter as a full BODY (the same avatar mesh the crowd would
 *     instance, via EnvironmentModule.bodyFor) with the weapon GLB in hand
 *     and a health plate (hp + morale, role, side colour) over the head;
 *     EnvironmentModule's crowd skips those ids (`hiddenIds`);
 *   - follows each body along the live versona position (marching columns
 *     really walk) with a speed governor, poses it per state (stride, aim,
 *     lunge, kneel-to-heal, rout run, fallen = lying where it fell);
 *   - fires combat VFX from `battle_event` INSERTS (war rework 2026-09-16):
 *     a shot (muzzle flash + bolt; a HIT impacts, a MISS flies past, a
 *     BLOCKED shot dies on the wall between), a heal (green bloom), a
 *     withdrawal / objective change / rout as a toast;
 *   - poses suppression from `unit_condition` (suppressed = crouch, pinned =
 *     heads down) and a withdrawing column's run;
 *   - drifts smoke over a joined or finished field (the text standard is
 *     GONE — battles live on the world map as red dots, 2026-09-16);
 *   - dresses every body from its `battle_look` (archetype + civ + name) so
 *     the FALLEN keep their real face after their versona rows are reaped;
 *   - toasts the player as the war moves: march → joined → resolved;
 *   - exposes `window.__battle` for probes.
 * Nothing here decides anything — every number is the server's.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { makeSystem, Phase } from '../../engine-compat.js';
import { PLAN_LABEL, type BattlePlan } from '@shared/living_events.js';
import { WEAPONS, FISTS } from '../../lib/weapon_catalog.js';
import { prefabAssetId } from '../../lib/player_models.js';
import type { AssetModule } from '../AssetModule.js';
import type { EnvironmentModule } from '../EnvironmentModule.js';
import { CombatVfx } from './CombatVfx.js';
import { makePlate, type Plate } from './HealthBars.js';
import { glowTexture, spriteOf } from './fx_textures.js';
import { disposeLater } from '../../lib/gpu_dispose.js';
import { sfxAt, sfxLoop } from '../../lib/sfx.js';
import { reportThreat } from '../../lib/audio/threat.js';

export interface BattleRow {
  battleId: bigint; warId: bigint; worldId: bigint; aggressorSid: bigint; defenderSid: bigint; phase: string; kind: string;
  targetX: number; targetZ: number; generalA: bigint; generalB: bigint; planA: string; planB: string; round: number;
  strengthA: number; strengthB: number; winner: string; summary: string;
  startedAt: { microsSinceUnixEpoch: bigint }; engagedAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint }; updatedAt: { microsSinceUnixEpoch: bigint };
}
export interface UnitRow {
  unitId: bigint; battleId: bigint; worldId: bigint; versonaId: bigint; side: string; role: string; weaponKey: string;
  hp: number; morale: number; state: string; x: number; z: number; targetId: bigint; lastHit: number; updatedAt: { microsSinceUnixEpoch: bigint };
}

export interface BattleEventRow { eventId: bigint; battleId: bigint; worldId: bigint; seq: number; kind: string; actorId: bigint; targetId: bigint; x: number; z: number; tx: number; tz: number; weaponKey: string; dmg: number; detail: string; at: { microsSinceUnixEpoch: bigint } }
export interface CondRow { versonaId: bigint; worldId: bigint; battleId: bigint; suppression: number; cover: number; fatigue: number; injuryTier: number }
export interface LookRow { lookId: bigint; battleId: bigint; versonaId: bigint; archetype: string; civ: string; name: string }

export const BATTLE_RANGE_M = 1500;
export const BODY_RANGE_M = 700;
const ROLE_LABEL: Record<string, string> = { general: 'General', captain: 'Captain', attacker: 'Attacker', defender: 'Defender', skirmisher: 'Skirmisher', medic: 'Medic', mechanic: 'Mechanic' };
const DRAWN = new Set(['marching', 'fighting', 'healing', 'withdrawing', 'routed', 'fallen', 'retreating', 'recovering', 'hiding', 'repairing']); // + the campaign's outpost/hiding states

interface Body {
  group: THREE.Group;
  mesh: THREE.Object3D;
  /** true while the body wears the capsule fallback (upgrade pending). */
  capsule: boolean;
  nextUpgradeT: number;
  versonaId: string;
  unitId: string;
  plate: Plate;
  pos: THREE.Vector3;
  yaw: number;
  lying: number;
  phase: number;
  recoil: number;
  lunge: number;
  weaponKey: string;
  weaponObj: THREE.Object3D | null;
  /** The asset id whose cache ref this body currently holds (released on drop/re-arm). */
  weaponAssetId: bigint | null;
  loadingKey: string;
  row: UnitRow;
  frozen: boolean;
  /** a duck on being suppressed (decays) */
  duck: number;
  /** seconds left of a reload beat (the plate says so) */
  reloadT: number;
}
interface Standard { smoke: Array<{ s: THREE.Sprite; age: number; life: number; ox: number; oz: number }>; x: number; z: number; y: number }
interface PendingFx { attacker: string; target: string; dmg: number; weaponKey: string; miss: boolean; tx: number; tz: number }

export class BattleModule implements IModule {
  readonly name = 'Battle';
  private readonly root = new THREE.Group();
  readonly vfx = new CombatVfx();
  private readonly bodies = new Map<string, Body>();       // unitId → body
  private readonly byVersona = new Map<string, Body>();    // versonaId → body
  private readonly standards = new Map<string, Standard>();
  private pendingEvents: BattleEventRow[] = [];
  private readonly conditions = new Map<string, CondRow>();
  private condAt = 0;
  private readonly lastEvents: string[] = [];
  private readonly looks = new Map<string, LookRow>();
  private subscribed = false;
  /** probe surface: did the subscription apply, and did it error? */
  private subDiag = { applied: false, error: '' , appliedAt: 0 };
  private disposed = false;
  private readonly _v = new THREE.Vector3();
  private readonly _w = new THREE.Vector3();
  private readonly toastedPhase = new Map<string, string>();

  constructor(private readonly env: EnvironmentModule, private readonly assets: AssetModule) {}

  init(app: App): void {
    this.root.name = 'battles';
    app.world.scene.add(this.root);
    app.world.scene.add(this.vfx.root);
    app.world.addSystem(makeSystem('BattleRender', Phase.Animation, 41, (dt) => this.frame(dt)));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    (window as unknown as Record<string, unknown>).__battle = {
      battles: () => this.battleRows().map((b) => ({
        id: b.battleId.toString(), warId: b.warId.toString(), phase: b.phase, kind: b.kind, round: b.round, planA: b.planA, planB: b.planB,
        strengthA: b.strengthA / 100, strengthB: b.strengthB / 100, winner: b.winner, summary: b.summary, x: b.targetX, z: b.targetZ,
        dist: Math.round(Math.hypot(b.targetX - atlas.localX, b.targetZ - atlas.localZ)),
      })),
      units: () => this.unitRows().map((u) => ({
        unitId: u.unitId.toString(), battleId: u.battleId.toString(), versonaId: u.versonaId.toString(), side: u.side, role: u.role, weaponKey: u.weaponKey,
        hp: u.hp, morale: u.morale, state: u.state, x: u.x, z: u.z, targetId: u.targetId.toString(), lastHit: u.lastHit,
      })),
      bodies: () => [...this.bodies.values()].map((b) => ({
        unitId: b.unitId, versonaId: b.versonaId, state: b.row.state, x: Math.round(b.pos.x * 10) / 10, z: Math.round(b.pos.z * 10) / 10,
        weapon: !!b.weaponObj, plate: b.plate.sprite.visible, lying: Math.round(b.lying * 100) / 100,
      })),
      standards: () => this.standards.size,
      looks: () => this.looks.size,
      fx: () => ({ ...this.vfx.stats, live: this.vfx.count, pending: this.pendingEvents.length }),
      events: () => [...this.lastEvents],
      conditions: () => [...this.conditions.values()].map((c) => ({ versonaId: c.versonaId.toString(), suppression: +c.suppression.toFixed(3), cover: c.cover, injuryTier: c.injuryTier })),
      hidden: () => this.env.hiddenIds.size,
      diag: () => ({ subscribed: this.subscribed, ...this.subDiag, rows: this.battleRows().length, units: this.unitRows().length, at: [Math.round(atlas.localX), Math.round(atlas.localZ)], active: (atlas.conn as unknown as { isActive?: boolean } | null)?.isActive ?? null }),
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try {
      conn.subscriptionBuilder()
        .onApplied(() => { this.subDiag.applied = true; this.subDiag.appliedAt = Date.now(); })
        .onError((ctx) => { const ev = (ctx as { event?: unknown }).event; this.subDiag.error = String((ev as Error)?.message ?? ev ?? 'subscription error'); })
        .subscribe(['SELECT * FROM battle', 'SELECT * FROM battle_unit', 'SELECT * FROM battle_event', 'SELECT * FROM unit_condition', 'SELECT * FROM battle_objective', 'SELECT * FROM battle_look']);
      const lookTbl = (conn.db as unknown as { battleLook?: { onInsert(cb: (c: unknown, row: LookRow) => void): void; onDelete(cb: (c: unknown, row: LookRow) => void): void; iter(): Iterable<LookRow> } }).battleLook;
      lookTbl?.onInsert((_c, row) => this.looks.set(row.versonaId.toString(), row));
      lookTbl?.onDelete((_c, row) => { if (this.looks.get(row.versonaId.toString())?.lookId === row.lookId) this.looks.delete(row.versonaId.toString()); });
      conn.db.battle.onInsert((_c, row) => this.toast(row as BattleRow));
      conn.db.battle.onUpdate((_c, _old, row) => this.toast(row as BattleRow));
      // The event stream (war rework): every ACTION is an insert — never a lastHit delta.
      conn.db.battleEvent.onInsert((_c, row) => {
        const e = row as BattleEventRow;
        if (e.worldId !== atlas.currentWorldId) return;
        if (Math.hypot(e.x - atlas.localX, e.z - atlas.localZ) > BODY_RANGE_M) return;
        this.pendingEvents.push(e);
      });
    } catch { /* pre-publish server */ }
  }

  private toast(b: BattleRow): void {
    const d = Math.hypot(b.targetX - atlas.localX, b.targetZ - atlas.localZ);
    if (d > 4000) return;
    const key = b.battleId.toString();
    if (this.toastedPhase.get(key) === b.phase) return;
    this.toastedPhase.set(key, b.phase);
    const where = `${Math.round(d)} m away`;
    const text = b.phase === 'march'
      ? `⚔ War: a militia is on the march — ${b.kind === 'siege' ? 'a SIEGE of the town' : 'the militias will meet on the road'} (${where})`
      : b.phase === 'engaged'
        ? `⚔ Battle joined ${where} — ${PLAN_LABEL[b.planA as BattlePlan]} against ${PLAN_LABEL[b.planB as BattlePlan]}`
        : `⚔ ${b.summary || 'the battle is over'} (${where})`;
    bus.emit('atlas:status', { text });
  }

  private battleRows(): BattleRow[] {
    const conn = atlas.conn;
    const out: BattleRow[] = [];
    if (!conn) return out;
    try { for (const b of conn.db.battle.iter()) out.push(b as BattleRow); } catch { /* pre-publish */ }
    return out;
  }
  private unitRows(): UnitRow[] {
    const conn = atlas.conn;
    const out: UnitRow[] = [];
    if (!conn) return out;
    try { for (const u of conn.db.battleUnit.iter()) out.push(u as UnitRow); } catch { /* pre-publish */ }
    return out;
  }

  private frame(dt: number): void {
    if (this.disposed) return;
    const t = performance.now() / 1000;
    const px = atlas.localX, pz = atlas.localZ;
    // Battles in range → standards; the set of battles whose units we draw.
    const near = new Map<string, BattleRow>();
    const seenStd = new Set<string>();
    let dinD = Infinity; // nearest ENGAGED battle → the distant war din
    let dinX = 0, dinZ = 0;
    for (const b of this.battleRows()) {
      if (b.worldId !== atlas.currentWorldId) continue; // (bug sweep) a Commons battle rendered into personal worlds near origin
      const d = Math.hypot(b.targetX - px, b.targetZ - pz);
      if (d > BATTLE_RANGE_M) continue;
      if (b.phase === 'engaged' && d < dinD) { dinD = d; dinX = b.targetX; dinZ = b.targetZ; }
      const key = b.battleId.toString();
      near.set(key, b);
      seenStd.add(key);
      this.updateStandard(key, b, t, dt);
    }
    // NO RING ON THE GROUND (2026-09-20). The objective was drawn as a coloured
    // circle laid on the terrain. It read as a UI decal painted over the world —
    // the one thing in the battle that was not a thing you could walk up to. The
    // `battle_objective` ROW still drives who controls what; it just is not
    // drawn any more. The smoke, the standards and the fighters carry the field.
    // Conditions (suppression / cover) once a second, bounded to the near battles.
    if (t - this.condAt > 1) {
      this.condAt = t;
      this.conditions.clear();
      const conn = atlas.conn;
      if (conn && near.size > 0) {
        try { for (const c of (conn.db as unknown as { unitCondition: { iter(): Iterable<CondRow> } }).unitCondition.iter()) if (near.has(c.battleId.toString())) this.conditions.set(c.versonaId.toString(), c); } catch { /* pre-publish */ }
      }
    }
    // THE DIN COMES FROM THE BATTLE (2026-09-20): the loop sits AT the nearest engaged field, so a war over the ridge is
    // over the ridge — it used to be centred mono that only grew louder. The level is still this module's own curve
    // (the panner gives direction only; one distance owner).
    const din = sfxLoop('sfx_battle_din', 'battle');
    if (dinD < BATTLE_RANGE_M) din.at({ x: dinX, y: atlas.localY + 2, z: dinZ });
    din.set(dinD < BATTLE_RANGE_M ? Math.min(0.35, Math.max(0.04, 0.4 - dinD * 0.00028)) : 0, 1, 0.6);
    // Standing IN a fight is combat to the score (PvP, the hunter and wars used to play under exploration silence).
    // Merely being near one is not: 60 m is inside the lines' own reach.
    if (dinD < 60) reportThreat('battle', performance.now());
    for (const [key, s] of this.standards) {
      if (seenStd.has(key)) continue;
      for (const sm of s.smoke) sm.s.parent?.remove(sm.s); // shared material
      this.standards.delete(key);
    }
    // Units → bodies.
    const seenBody = new Set<string>();
    if (near.size > 0) {
      for (const u of this.unitRows()) {
        const b = near.get(u.battleId.toString());
        if (!b || !DRAWN.has(u.state)) continue;
        const live = this.env.versonaPosition(u.versonaId.toString());
        const ux = live?.x ?? u.x, uz = live?.z ?? u.z;
        if (Math.hypot(ux - px, uz - pz) > BODY_RANGE_M) continue;
        const key = u.unitId.toString();
        seenBody.add(key);
        let body = this.bodies.get(key);
        if (!body) { body = this.makeBody(u); this.bodies.set(key, body); this.byVersona.set(body.versonaId, body); }
        body.row = u;
        this.updateBody(body, b, live, t, dt);
      }
    }
    for (const [key, body] of this.bodies) {
      if (seenBody.has(key)) continue;
      this.dropBody(body);
      this.bodies.delete(key);
    }
    // The crowd skips the versonas we draw.
    this.env.hiddenIds.clear();
    for (const body of this.bodies.values()) this.env.hiddenIds.add(body.versonaId); // fallen included — this module draws them lying (double-draw fix)
    // Shots, blows, heals queued by row updates.
    if (this.pendingEvents.length) { const q = this.pendingEvents; this.pendingEvents = []; for (const e of q) this.playEvent(e); }
    this.vfx.update(dt);
  }

  // ── Bodies ───────────────────────────────────────────────────────────────

  /** The real crowd body when the versona is animated nearby; else the body its LOOK names; else the capsule (masters still streaming). */
  private bodyOf(u: UnitRow): THREE.Group {
    const vid = u.versonaId.toString();
    let group = this.env.bodyFor(vid);
    if (group.userData.capsule === true) {
      const look = this.looks.get(vid);
      if (look) group = this.env.bodyForLook(look.archetype, u.versonaId, look.civ || null);
    }
    return group;
  }

  private makeBody(u: UnitRow): Body {
    const vid = u.versonaId.toString();
    const group = this.bodyOf(u);
    const mesh = group.children[0] ?? group;
    const capsule = group.userData.capsule === true;
    const live = this.env.versonaPosition(vid);
    const pos = new THREE.Vector3(live?.x ?? u.x, live?.y ?? this.env.groundAt(u.x, u.z), live?.z ?? u.z);
    group.position.copy(pos);
    this.root.add(group);
    const plate = makePlate();
    plate.sprite.visible = false;
    this.root.add(plate.sprite);
    const body: Body = {
      group, mesh, capsule, nextUpgradeT: 0, versonaId: vid, unitId: u.unitId.toString(), plate, pos, yaw: 0, lying: 0,
      phase: (Number(u.versonaId % 997n) / 997) * Math.PI * 2, recoil: 0, lunge: 0, weaponKey: '', weaponObj: null, weaponAssetId: null, loadingKey: '', row: u, frozen: false, duck: 0, reloadT: 0,
    };
    return body;
  }

  private dropBody(body: Body): void {
    body.group.parent?.remove(body.group);
    body.plate.dispose();
    this.releaseWeapon(body);
    body.weaponKey = '';
    body.loadingKey = ''; // an in-flight arm() must see a dropped body, not land its ref on an orphan (2026-09-12)
    this.byVersona.delete(body.versonaId);
  }

  /** Hand back the weapon GLB's cache reference (if this body holds one). */
  private releaseWeapon(body: Body): void {
    if (body.weaponAssetId !== null) { this.assets.dispose(body.weaponAssetId); body.weaponAssetId = null; }
  }

  private updateBody(body: Body, b: BattleRow, live: { x: number; y: number; z: number } | null, t: number, dt: number): void {
    // PILL FIX (war rework): a capsule-fallback body retries the real civ
    // avatar every ~2 s and upgrades IN PLACE once the crowd GLB exists
    // (the VersonaTravel upgrade-in-place idiom).
    if (body.capsule && t > body.nextUpgradeT) {
      body.nextUpgradeT = t + 2;
      const fresh = this.bodyOf(body.row);
      if (fresh.userData.capsule !== true) {
        fresh.position.copy(body.group.position);
        fresh.rotation.copy(body.group.rotation);
        this.root.remove(body.group);
        this.root.add(fresh);
        body.group = fresh;
        body.mesh = fresh.children[0] ?? fresh;
        body.capsule = false;
        // The weapon was parented to the OLD group — release it; the normal
        // per-frame weapon sync re-attaches to the fresh body.
        if (body.weaponObj) { body.weaponObj.parent?.remove(body.weaponObj); body.weaponObj = null; }
        this.releaseWeapon(body);
        body.weaponKey = '';
        body.loadingKey = '';
      }
    }
    const u = body.row;
    // Target position: the live body when the versona is in the AOI window,
    // else the server's combat snapshot. The fallen stay where they fell.
    if (u.state === 'fallen') body.frozen = true;
    if (!body.frozen) {
      const tx = live?.x ?? u.x, tz = live?.z ?? u.z;
      const ty = live?.y ?? this.env.groundAt(tx, tz);
      const dx = tx - body.pos.x, dz = tz - body.pos.z;
      const dist = Math.hypot(dx, dz);
      const maxStep = (u.state === 'routed' ? 5.5 : u.state === 'retreating' ? 4.8 : u.state === 'withdrawing' ? 3.2 : 4.2) * dt;
      if (dist > 40) body.pos.set(tx, ty, tz);                 // a true teleport — accept
      else if (dist > maxStep) { body.pos.x += (dx / dist) * maxStep; body.pos.z += (dz / dist) * maxStep; body.pos.y = this.env.groundAt(body.pos.x, body.pos.z); }
      else body.pos.set(tx, ty, tz);
      if (dist > 0.05) body.yaw = lerpAngle(body.yaw, Math.atan2(dx, dz), Math.min(1, dt * 6));
    }
    // Facing: in a fight, square up on the target (or the nearest foe body).
    if (u.state === 'fighting' || u.state === 'healing') {
      const tgt = this.byVersona.get(u.targetId.toString());
      let fx = tgt?.pos.x, fz = tgt?.pos.z;
      if (fx === undefined || fz === undefined) {
        let bd = 30 * 30;
        for (const o of this.bodies.values()) {
          if (o === body || o.row.side === u.side || o.row.state === 'fallen') continue;
          const d = (o.pos.x - body.pos.x) ** 2 + (o.pos.z - body.pos.z) ** 2;
          if (d < bd) { bd = d; fx = o.pos.x; fz = o.pos.z; }
        }
      }
      if (fx !== undefined && fz !== undefined) body.yaw = lerpAngle(body.yaw, Math.atan2(fx - body.pos.x, fz - body.pos.z), Math.min(1, dt * 8));
    }
    // Pose.
    let pitch = 0, bob = 0;
    const spec = WEAPONS[u.weaponKey] ?? FISTS;
    const cond = this.conditions.get(u.versonaId.toString());
    const sup = cond?.suppression ?? 0;
    body.duck = Math.max(0, body.duck - dt * 0.8);
    if (u.state === 'marching') { bob = Math.abs(Math.sin(t * 7 + body.phase)) * 0.07; pitch = 0.06; }
    else if (u.state === 'routed') { bob = Math.abs(Math.sin(t * 9 + body.phase)) * 0.1; pitch = 0.14; }
    else if (u.state === 'withdrawing') { bob = Math.abs(Math.sin(t * 8 + body.phase)) * 0.08; pitch = 0.1; }
    else if (u.state === 'healing') { pitch = 0.32 + Math.sin(t * 2 + body.phase) * 0.03; }
    else if (u.state === 'retreating') { bob = Math.abs(Math.sin(t * 9 + body.phase)) * 0.09; pitch = 0.16; }   // a wounded run for the medics
    else if (u.state === 'recovering') { pitch = 0.48; bob = -0.35; }                                              // sat at the aid station
    else if (u.state === 'hiding') { pitch = 0.62; bob = -0.42; }                                                  // flat behind a wall, going still
    else if (u.state === 'repairing') { pitch = 0.3 + Math.sin(t * 3 + body.phase) * 0.05; }                     // bent over a hull at the bay
    else if (u.state === 'fighting') {
      if (sup >= 0.85) pitch = 0.55;                                   // pinned: heads down
      else if (sup >= 0.6 || body.duck > 0.5) pitch = 0.38;            // suppressed: a crouch behind whatever is there
      else if (spec.kind === 'gun') { pitch = 0.05 + body.recoil * 0.12; }
      else { const l = Math.max(0, Math.sin(t * 5.2 + body.phase)); pitch = 0.12 + l * 0.2 + body.lunge * 0.25; bob = l * 0.08; }
    }
    body.recoil = Math.max(0, body.recoil - dt * 6);
    body.lunge = Math.max(0, body.lunge - dt * 4);
    body.reloadT = Math.max(0, body.reloadT - dt);
    body.lying = u.state === 'fallen' ? Math.min(1, body.lying + dt * 1.6) : 0;
    body.group.position.set(body.pos.x, body.pos.y + bob - body.lying * 0.12, body.pos.z);
    body.group.rotation.set(pitch * (1 - body.lying) - (Math.PI / 2) * body.lying, body.yaw, 0, 'YXZ');
    // Weapon in hand (loaded once per key).
    if (u.weaponKey !== body.weaponKey) this.arm(body, u.weaponKey);
    // Health plate: shown while the fight is real (not on the march).
    const showPlate = u.state !== 'marching' || b.phase !== 'march';
    body.plate.sprite.visible = showPlate;
    if (showPlate) {
      body.plate.sprite.position.set(body.pos.x, body.pos.y + 2.35 - body.lying * 1.7, body.pos.z);
      body.plate.draw({
        name: `${this.looks.get(u.versonaId.toString())?.name ?? `#${u.versonaId}`} · ${ROLE_LABEL[u.role] ?? u.role}`,
        sub: u.state === 'withdrawing' ? 'withdrawing' : u.state === 'retreating' ? 'wounded — to the medics' : u.state === 'recovering' ? 'at the aid station' : u.state === 'hiding' ? 'hiding' : u.state === 'repairing' ? 'at the bay'
          : body.reloadT > 0 ? `${spec.name} · reloading` : sup >= 0.85 ? 'PINNED' : sup >= 0.6 ? 'suppressed' : cond && cond.cover > 0 ? `${spec.name} · in cover` : spec.name,
        hp: u.hp, morale: u.morale,
        side: u.side === 'a' ? 'a' : 'b', state: u.state,
      });
    }
  }

  private arm(body: Body, key: string): void {
    body.weaponKey = key;
    if (body.weaponObj) { body.weaponObj.parent?.remove(body.weaponObj); body.weaponObj = null; }
    this.releaseWeapon(body);
    const spec = WEAPONS[key];
    const conn = atlas.conn;
    if (!spec || !conn) return;
    const id = prefabAssetId(conn, `prop:weapon_${key}`);
    if (id === null) return;
    body.loadingKey = key;
    void this.assets.load(id).then((master) => {
      // Every load() is a cache refcount; a superseded or disposed body must
      // hand it back or the weapon GLB becomes immortal in VRAM.
      if (body.loadingKey !== key || body.weaponKey !== key || this.disposed) { this.assets.dispose(id); return; }
      this.releaseWeapon(body);
      body.weaponAssetId = id;
      const obj = master.clone();
      const box = new THREE.Box3().setFromObject(obj);
      const len = Math.max(0.01, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
      obj.scale.setScalar((spec.kind === 'gun' ? 0.8 : 1.1) / len);
      obj.position.set(0.34, 1.02, 0.18);
      // Guns: muzzle FORWARD (the -π/2 seat held every rifle backwards).
      obj.rotation.set(0, spec.kind === 'gun' ? Math.PI / 2 : -Math.PI / 2, spec.kind === 'melee' ? -Math.PI / 5 : 0);
      body.group.add(obj);
      body.weaponObj = obj;
    }).catch(() => { /* icon-less militia: bare hands */ });
  }

  // ── Shots, blows, heals ──────────────────────────────────────────────────

  private playFx(p: PendingFx): void {
    const a = this.byVersona.get(p.attacker);
    if (!a) return;
    const tgt = this.byVersona.get(p.target);
    const spec = WEAPONS[p.weaponKey] ?? FISTS;
    const hand = this._v.set(a.pos.x + Math.sin(a.yaw) * 0.45, a.pos.y + 1.15, a.pos.z + Math.cos(a.yaw) * 0.45);
    const dFx = Math.hypot(a.pos.x - atlas.localX, a.pos.z - atlas.localZ);
    const gFx = Math.max(0, 0.45 - dFx * 0.0009); // audible to ~500 m
    if (p.dmg < 0) {
      if (tgt) this.vfx.heal(this._w.set(tgt.pos.x, tgt.pos.y + 1, tgt.pos.z));
      if (gFx > 0.05 && tgt) sfxAt('sfx_heal_use', { x: tgt.pos.x, y: tgt.pos.y + 1, z: tgt.pos.z }, { gain: gFx * 0.5, cooldownMs: 400, bus: 'npcFoley', priority: 35 });
      return;
    }
    let chest = tgt ? this._w.set(tgt.pos.x + (Math.random() - 0.5) * 0.3, tgt.pos.y + 0.9 + Math.random() * 0.5, tgt.pos.z + (Math.random() - 0.5) * 0.3) : null;
    if (p.miss) {
      // A miss is visibly a miss: the bolt flies 6 m PAST the mark (or the server's target point) and nothing impacts.
      const ax = a.pos.x, az = a.pos.z;
      const mx = tgt?.pos.x ?? p.tx, mz = tgt?.pos.z ?? p.tz;
      const dx = mx - ax, dz = mz - az, d = Math.hypot(dx, dz) || 1;
      chest = this._w.set(mx + (dx / d) * 6 + (Math.random() - 0.5) * 1.2, (tgt?.pos.y ?? a.pos.y) + 1.1 + Math.random() * 0.6, mz + (dz / d) * 6 + (Math.random() - 0.5) * 1.2);
    }
    if (spec.kind === 'gun') {
      a.recoil = 1;
      const tint = spec.tier >= 4 ? 0xc9a6ff : spec.tier === 3 ? 0x9fe8ff : 0xffd27a;
      this.vfx.muzzle(hand, tint);
      // Jittered rate so twenty shooters never phase-lock into one tone.
      // AT the shooter. No `emitter`: the cooldown stays per-KEY on purpose — it is the battle's shot-density cap (≤ 11/s).
      if (gFx > 0.04) sfxAt('sfx_battle_shot', { x: a.pos.x, y: a.pos.y + 1.2, z: a.pos.z }, { gain: gFx, rate: 0.9 + Math.random() * 0.25, cooldownMs: 90, caption: 'Gunfire' });
      if (chest) {
        this.vfx.bolt(hand, chest, tint);
        if (p.miss) { /* it flies on — no impact on a mark it did not hit */ }
        else if (p.dmg >= 34 || spec.tier >= 4) { this.vfx.explosion(chest, 0.9, tint); if (gFx > 0.04) sfxAt('sfx_battle_explosion', { x: chest.x, y: chest.y, z: chest.z }, { gain: gFx, cooldownMs: 350, caption: 'Explosion' }); }
        else this.vfx.impact(chest, 0.45 + p.dmg / 80, 0xffc080);
      }
    } else {
      a.lunge = 1;
      if (chest && !p.miss) { this.vfx.clash(chest, spec.tier >= 4 ? 0xc9a6ff : 0xfff3c0); if (gFx > 0.04) sfxAt('sfx_melee_clash', { x: chest.x, y: chest.y, z: chest.z }, { gain: gFx, rate: 0.92 + Math.random() * 0.18, cooldownMs: 140 }); }
    }
  }

  /** One battle_event → its presentation (the server already decided everything). */
  private playEvent(e: BattleEventRow): void {
    if (this.lastEvents.push(`${e.seq}:${e.kind}${e.detail ? ':' + e.detail : ''}`) > 20) this.lastEvents.shift();
    const a = this.byVersona.get(e.actorId.toString());
    const tgt = this.byVersona.get(e.targetId.toString());
    switch (e.kind) {
      case 'shot': {
        if (!a) return;
        this.playFx({ attacker: e.actorId.toString(), target: e.targetId.toString(), dmg: e.detail === 'hit' ? Math.max(1, e.dmg) : 0, weaponKey: e.weaponKey, miss: e.detail !== 'hit', tx: e.tx, tz: e.tz });
        return;
      }
      case 'heal': if (a) this.playFx({ attacker: e.actorId.toString(), target: e.targetId.toString(), dmg: -1, weaponKey: '', miss: false, tx: e.tx, tz: e.tz }); return;
      case 'blocked': {
        // The bolt dies on the wall between them: a spark at the midpoint, no impact on the mark.
        if (!a) return;
        const mx = (e.x + e.tx) / 2, mz = (e.z + e.tz) / 2;
        const mid = this._w.set(mx, this.env.groundAt(mx, mz) + 1.2, mz);
        const hand = this._v.set(a.pos.x + Math.sin(a.yaw) * 0.45, a.pos.y + 1.15, a.pos.z + Math.cos(a.yaw) * 0.45);
        a.recoil = 1;
        this.vfx.muzzle(hand, 0xffd27a);
        this.vfx.bolt(hand, mid, 0xffd27a);
        this.vfx.impact(mid, 0.4, 0xcfc4a8);
        return;
      }
      case 'suppressed': if (tgt) tgt.duck = 1; return;
      case 'reload': if (a) a.reloadT = 3; return;
      case 'turret': {
        // The outpost guns: a bolt from the turret at (x, z) to the mark; the OutpostModule swings the turret itself.
        const from = this._v.set(e.x, this.env.groundAt(e.x, e.z) + 2.05, e.z);
        const to = this._w.set(e.tx, this.env.groundAt(e.tx, e.tz) + 1.1, e.tz);
        this.vfx.muzzle(from, 0xffe08a);
        this.vfx.bolt(from, to, 0xffe08a);
        if (e.detail === 'hit') { this.vfx.impact(to, 0.6, 0xffb060); if (tgt) tgt.duck = 1; }
        else { const past = to.clone().addScaledVector(to.clone().sub(from).normalize(), 6); this.vfx.bolt(to, past, 0xffe08a); }
        return;
      }
      case 'withdraw': case 'objective': case 'routed': case 'order': case 'reinforce': case 'lesson': case 'advice': {
        if (Math.hypot(e.x - atlas.localX, e.z - atlas.localZ) > 900) return;
        const sideName = (s: string): string => (s === 'a' ? 'the attackers' : 'the defenders');
        const text = e.kind === 'withdraw' ? `⚔ ${e.detail === 'a' ? 'The attackers' : 'The defenders'} withdraw — the column turns for home`
          : e.kind === 'objective' ? `⚔ The objective changes hands — ${e.detail === 'a' ? 'the attackers' : e.detail === 'b' ? 'the defenders' : 'no one'} hold it`
          : e.kind === 'routed' ? '⚔ A fighter breaks and runs'
          : e.kind === 'reinforce' ? (e.detail.endsWith(':called') ? `⚔ ${sideName(e.detail[0]).replace(/^t/, 'T')} send for relief` : e.detail.includes(':sent:') ? `⚔ ${e.dmg} fighters take ship to relieve ${sideName(e.detail[0])}` : '')
          : e.kind === 'lesson' ? `⚔ A general takes the lesson home: ${e.detail.startsWith('won') ? 'what won it' : 'what lost it'} — ${PLAN_LABEL[e.detail.split(':')[1] as BattlePlan] ?? e.detail}`
          : e.kind === 'advice' ? `⚔ The council advises ${sideName(e.detail[0])}: ${PLAN_LABEL[e.detail.split(':')[1] as BattlePlan] ?? e.detail}`
          : e.detail.startsWith('squad:') ? `⚔ A captain orders the squad: ${PLAN_LABEL[e.detail.split(':')[2] as BattlePlan] ?? e.detail}`
          : e.detail === 'relief arrived' ? '⚔ The relief is on the field — the line takes heart'
          : `⚔ New orders: ${PLAN_LABEL[e.detail as BattlePlan] ?? e.detail}`;
        if (text) bus.emit('atlas:status', { text });
        return;
      }
      default: return;
    }
  }

  // ── The objective ring ───────────────────────────────────────────────────

  // ── The standard over the field ──────────────────────────────────────────

  /** Drifting smoke over a joined or finished field (the text standard is gone — the map carries the battle). */
  private updateStandard(key: string, b: BattleRow, t: number, dt: number): void {
    let s = this.standards.get(key);
    if (!s) {
      s = { smoke: [], x: b.targetX, z: b.targetZ, y: this.env.groundAt(b.targetX, b.targetZ) };
      this.standards.set(key, s);
    }
    // Smoke drifts over a joined or finished field.
    const want = b.phase === 'march' ? 0 : 4;
    while (s.smoke.length < want) {
      const sp = spriteOf(glowTexture(), 0x2e2a28, 1.2, false, 0.35);
      const sm = { s: sp, age: Math.random() * 5, life: 5 + Math.random() * 3, ox: (Math.random() - 0.5) * 14, oz: (Math.random() - 0.5) * 14 };
      this.root.add(sp);
      s.smoke.push(sm);
    }
    for (const sm of s.smoke) {
      sm.age += dt;
      if (sm.age >= sm.life) { sm.age = 0; sm.ox = (Math.random() - 0.5) * 14; sm.oz = (Math.random() - 0.5) * 14; }
      const k = sm.age / sm.life;
      sm.s.position.set(s.x + sm.ox + Math.sin(t * 0.4 + sm.ox) * 0.8, this.env.groundAt(s.x + sm.ox, s.z + sm.oz) + 0.5 + sm.age * 1.4, s.z + sm.oz);
      sm.s.scale.setScalar((1.2 + sm.age * 0.9) * Math.max(0.01, Math.sin(k * Math.PI))); // fade by scale (shared material)
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const body of this.bodies.values()) this.dropBody(body);
    this.bodies.clear();
    this.env.hiddenIds.clear();
    for (const s of this.standards.values()) { for (const sm of s.smoke) sm.s.parent?.remove(sm.s); }
    this.standards.clear();
    this.vfx.dispose();
    this.root.parent?.remove(this.root);
  }
}

function lerpAngle(a: number, b: number, k: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * k;
}
